/**
 * Utility functions for formatting dates, times and durations
 */

/**
 * Format a date string or Date object to a human-readable format
 * 
 * @param dateStr Date string or Date object
 * @param includeTime Whether to include time in the formatted string
 * @returns Formatted date string
 */
export function formatDate(dateStr: string | Date | undefined | null, includeTime = false): string {
  if (!dateStr) return 'N/A';
  
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Format to DD/MM/YYYY
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata', // IST timezone
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true; // Use 12-hour format with AM/PM
  }
  
  return date.toLocaleString('en-IN', options);
}

/**
 * Format minutes into a human-readable duration string (e.g. "2h 30m")
 * 
 * @param minutes Number of minutes
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number): string {
  if (!minutes || isNaN(minutes)) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
}

/**
 * Format just the time in 12-hour format with AM/PM
 * 
 * @param dateTime Date string or Date object
 * @returns Formatted time string (hh:mm AM/PM)
 */
export function formatTime(dateTime: string | Date | undefined | null): string {
  if (!dateTime) return 'N/A';
  
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Format to hh:mm AM/PM in IST timezone
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  };
  
  return date.toLocaleTimeString('en-IN', options);
}

/**
 * Calculate the difference between two dates in minutes
 * 
 * @param startDate Start date
 * @param endDate End date
 * @returns Number of minutes between the two dates
 */
export function calculateDuration(startDate: string | Date, endDate: string | Date): number {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  // Check if dates are valid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }
  
  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / (1000 * 60)); // Convert to minutes
}

/**
 * Calculate how many days remain until a deadline
 * 
 * @param deadline Deadline date
 * @returns Number of days remaining (negative if passed)
 */
export function getDaysRemaining(deadline: string | Date): number {
  const now = new Date();
  const deadlineDate = typeof deadline === 'string' ? new Date(deadline) : deadline;
  
  // Check if date is valid
  if (isNaN(deadlineDate.getTime())) {
    return 0;
  }
  
  const diffMs = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24)); // Convert to days
}

/**
 * Convert a date from any format to DD/MM/YYYY format
 * 
 * @param date Date string or Date object
 * @returns Date in DD/MM/YYYY format
 */
export function toFormattedDateString(date: string | Date | undefined | null): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  // Format to DD/MM/YYYY in IST timezone
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  };
  
  return dateObj.toLocaleDateString('en-IN', options);
}

/**
 * Parse a date string in DD/MM/YYYY format to a Date object
 * 
 * @param dateStr Date string in DD/MM/YYYY format
 * @returns Date object
 */
export function parseFormattedDate(dateStr: string): Date {
  // Check if dateStr is in DD/MM/YYYY format
  const dateParts = dateStr.split('/');
  if (dateParts.length === 3) {
    // Convert DD/MM/YYYY to YYYY-MM-DD for proper parsing
    const day = dateParts[0];
    const month = dateParts[1];
    const year = dateParts[2];
    return new Date(`${year}-${month}-${day}`);
  }
  
  // If not in expected format, fall back to standard parsing
  return new Date(dateStr);
}
